import { StoryProgress } from '../types/story';
import storyUnlocks from '../data/storyUnlocks.json';

interface UnlockReward {
  trustBonus: number;
  unlockNotes?: string;
}

interface UnlockCondition {
  completedChapter?: string;
  minTrust?: number;
  requiredEvidence?: string[];
}

interface StoryUnlock {
  id: string;
  title: string;
  surface: string;
  answer: string;
  hint: string;
  unlockCondition: UnlockCondition;
}

interface ChapterUnlocks {
  unlockedStories: StoryUnlock[];
  rewards: UnlockReward;
}

interface StoryUnlocks {
  [chapterId: string]: ChapterUnlocks;
}

export class StoryUnlockService {
  private static instance: StoryUnlockService;
  private unlockedStories: Set<string> = new Set();
  private availableStories: any[] = [];

  private constructor() {
    this.loadUnlockedStories();
  }

  static getInstance(): StoryUnlockService {
    if (!StoryUnlockService.instance) {
      StoryUnlockService.instance = new StoryUnlockService();
    }
    return StoryUnlockService.instance;
  }

  // 检查并解锁新内容
  checkUnlocks(progress: StoryProgress): UnlockReward[] {
    const rewards: UnlockReward[] = [];
    const completedChapter = progress.completedChapters[progress.completedChapters.length - 1];

    if (completedChapter && (storyUnlocks as StoryUnlocks)[completedChapter]) {
      const chapterUnlocks = (storyUnlocks as StoryUnlocks)[completedChapter];

      chapterUnlocks.unlockedStories.forEach((story: StoryUnlock) => {
        if (!this.unlockedStories.has(story.id)) {
          const condition = story.unlockCondition;
          let canUnlock = true;

          // 检查完成章节条件
          if (condition.completedChapter && !progress.completedChapters.includes(condition.completedChapter)) {
            canUnlock = false;
          }

          // 检查信任度条件
          if (condition.minTrust) {
            const maxTrust = Math.max(...Object.values(progress.trust));
            if (maxTrust < condition.minTrust) {
              canUnlock = false;
            }
          }

          // 检查证据条件
          if (condition.requiredEvidence) {
            const hasAllEvidence = condition.requiredEvidence.every((evidenceId: string) =>
              progress.evidence[evidenceId] === true
            );
            if (!hasAllEvidence) {
              canUnlock = false;
            }
          }

          if (canUnlock) {
            this.unlockStory(story.id);
            rewards.push(chapterUnlocks.rewards);
            this.availableStories.push(story);
          }
        }
      });
    }

    return rewards;
  }

  // 解锁故事
  private unlockStory(storyId: string): void {
    this.unlockedStories.add(storyId);
    this.saveUnlockedStories();
  }

  // 保存解锁状态
  private saveUnlockedStories(): void {
    localStorage.setItem('unlockedStories', JSON.stringify(Array.from(this.unlockedStories)));
  }

  // 加载解锁状态
  private loadUnlockedStories(): void {
    const saved = localStorage.getItem('unlockedStories');
    if (saved) {
      try {
        this.unlockedStories = new Set(JSON.parse(saved));
      } catch (e) {
        console.error('Failed to load unlocked stories:', e);
      }
    }
  }

  // 获取已解锁的故事
  getUnlockedStories(): any[] {
    return this.availableStories.filter(story =>
      this.unlockedStories.has(story.id)
    );
  }

  // 检查故事是否已解锁
  isStoryUnlocked(storyId: string): boolean {
    return this.unlockedStories.has(storyId);
  }

  // 重置解锁状态
  resetUnlocks(): void {
    this.unlockedStories.clear();
    this.availableStories = [];
    this.saveUnlockedStories();
  }
}